/**
 * Audio engine for the How It Works tour.
 *
 * Owns the background-music loop (with gentle fade in/out) and the UI sound
 * effects (whoosh / click / chime / finale). Browsers block autoplay until a
 * user gesture, so callers must invoke `unlock()` from the first interaction
 * (the tour's "click to begin") — after that, music starts automatically
 * whenever it is enabled. Preferences persist per user in localStorage.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TOUR_AUDIO } from './tourCore';

const MUSIC_PREF_KEY = 'belab.howItWorks.music';
const SFX_PREF_KEY = 'belab.howItWorks.sfx';
const MUSIC_VOLUME = 0.35;
const FADE_STEP_MS = 60;

type SfxName = 'whoosh' | 'click' | 'chime' | 'finale';

const readPref = (key: string): boolean => {
  try {
    return localStorage.getItem(key) !== 'off';
  } catch {
    return true;
  }
};

const writePref = (key: string, on: boolean) => {
  try {
    localStorage.setItem(key, on ? 'on' : 'off');
  } catch {
    /* private mode — session-only prefs */
  }
};

export interface TourAudio {
  musicOn: boolean;
  sfxOn: boolean;
  toggleMusic: () => void;
  toggleSfx: () => void;
  /** Call from the first user gesture; starts music if enabled. */
  unlock: () => void;
  play: (name: SfxName) => void;
  /** Fade the music out (used when leaving the tour). */
  fadeOutMusic: () => void;
}

export const useTourAudio = (): TourAudio => {
  const [musicOn, setMusicOn] = useState(() => readPref(MUSIC_PREF_KEY));
  const [sfxOn, setSfxOn] = useState(() => readPref(SFX_PREF_KEY));
  const unlockedRef = useRef(false);
  const musicRef = useRef<HTMLAudioElement | null>(null);
  const fadeTimerRef = useRef<number | null>(null);
  const musicOnRef = useRef(musicOn);
  const sfxOnRef = useRef(sfxOn);
  musicOnRef.current = musicOn;
  sfxOnRef.current = sfxOn;

  const sfx = useMemo(() => {
    const make = (src: string) => {
      const a = new Audio(src);
      a.preload = 'auto';
      return a;
    };
    return {
      whoosh: make(TOUR_AUDIO.whoosh),
      click: make(TOUR_AUDIO.click),
      chime: make(TOUR_AUDIO.chime),
      finale: make(TOUR_AUDIO.finale),
    };
  }, []);

  const clearFade = useCallback(() => {
    if (fadeTimerRef.current !== null) {
      window.clearInterval(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);

  const fadeMusicTo = useCallback(
    (target: number, onDone?: () => void) => {
      const music = musicRef.current;
      if (!music) return;
      clearFade();
      fadeTimerRef.current = window.setInterval(() => {
        const delta = target - music.volume;
        if (Math.abs(delta) <= 0.03) {
          music.volume = target;
          clearFade();
          onDone?.();
          return;
        }
        music.volume += Math.sign(delta) * 0.03;
      }, FADE_STEP_MS);
    },
    [clearFade],
  );

  const startMusic = useCallback(() => {
    if (!unlockedRef.current || !musicOnRef.current) return;
    let music = musicRef.current;
    if (!music) {
      music = new Audio(TOUR_AUDIO.music);
      music.loop = true;
      music.preload = 'auto';
      musicRef.current = music;
    }
    music.volume = music.paused ? 0 : music.volume;
    music.play().then(() => fadeMusicTo(MUSIC_VOLUME)).catch(() => {
      /* browser refused (no gesture yet) — retried on next unlock() */
    });
  }, [fadeMusicTo]);

  const stopMusic = useCallback(() => {
    const music = musicRef.current;
    if (!music || music.paused) return;
    fadeMusicTo(0, () => music.pause());
  }, [fadeMusicTo]);

  const unlock = useCallback(() => {
    if (!unlockedRef.current) {
      unlockedRef.current = true;
      startMusic();
    }
  }, [startMusic]);

  const toggleMusic = useCallback(() => {
    setMusicOn((prev) => {
      const next = !prev;
      writePref(MUSIC_PREF_KEY, next);
      musicOnRef.current = next;
      if (next) startMusic();
      else stopMusic();
      return next;
    });
  }, [startMusic, stopMusic]);

  const toggleSfx = useCallback(() => {
    setSfxOn((prev) => {
      const next = !prev;
      writePref(SFX_PREF_KEY, next);
      return next;
    });
  }, []);

  const play = useCallback(
    (name: SfxName) => {
      if (!sfxOnRef.current || !unlockedRef.current) return;
      const a = sfx[name];
      a.currentTime = 0;
      a.play().catch(() => {
        /* ignore — SFX are best-effort */
      });
    },
    [sfx],
  );

  const fadeOutMusic = useCallback(() => stopMusic(), [stopMusic]);

  // Teardown: stop everything when the tour unmounts.
  useEffect(() => {
    const music = () => musicRef.current;
    return () => {
      clearFade();
      music()?.pause();
    };
  }, [clearFade]);

  return { musicOn, sfxOn, toggleMusic, toggleSfx, unlock, play, fadeOutMusic };
};
